/**
 * Minimal, dependency-free event tracking.
 *
 * The project has no analytics vendor wired in. Rather than adding one, this
 * pushes campaign events onto `window.dataLayer` (the standard GTM queue) and
 * forwards to `gtag` when a tag is present. With neither present it is a no-op,
 * so pages can always call it safely. Only public, non-identifying values
 * (campaign name, athlete slug, placement) should ever be passed.
 */
import { isDemoRoute } from "@/lib/demoSafety";

export type CampaignEvent =
  | "campaign_view"
  | "roster_view"
  | "profile_view"
  | "share_click"
  | "back_click"
  | "email_signup_completed"
  | "qr_scan"
  | "follow_click"
  | "support_intent_click"
  | "support_handoff"
  | "inquiry_submitted"
  | "back_her_game_open"
  | "direct_support_click"
  | "shop_view";

export type EventParams = Record<string, string | number | boolean | undefined>;

type TrackWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};

let suppressed = false;
/** Admin/test previews call this so they never reach production reporting. */
export const setAnalyticsSuppressed = (value: boolean) => {
  suppressed = value;
};
export const isAnalyticsSuppressed = () => suppressed || (typeof window !== "undefined" && isDemoRoute(window.location.pathname));

export const trackEvent = (event: CampaignEvent, params: EventParams = {}): void => {
  if (typeof window === "undefined" || isAnalyticsSuppressed()) return;
  const w = window as TrackWindow;
  const payload = { event, ...params };
  try {
    w.dataLayer = w.dataLayer ?? [];
    w.dataLayer.push(payload);
    if (typeof w.gtag === "function") w.gtag("event", event, params);
  } catch {
    // Analytics must never break the page.
  }
};
